import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ListChecks, CheckCircle2, Loader2, PartyPopper } from "lucide-react";
import confetti from "canvas-confetti";

/**
 * StudioInlineTopBar — sticky 48 px chrome on top of the editor-mode sandbox.
 *
 * Renders:
 *   - Back link to /studio
 *   - Sandbox name + draft status
 *   - Progress segment "X / Y questions"
 *   - "Voir toutes les questions" button (opens the drawer)
 *   - "Finaliser & recevoir le PDF" CTA (publish)
 */
export default function StudioInlineTopBar({
  brandName,
  answered,
  total,
  isDraft,
  isSaving,
  isPublishing,
  onOpenDrawer,
  onPublish,
}) {
  const pct = total > 0 ? Math.min(100, Math.round((answered / total) * 100)) : 0;
  const isComplete = total > 0 && answered >= total;
  // Fire confetti ONCE — the very first time the user crosses the finish line in this session.
  // Persisted across renders via a ref so re-renders don't re-trigger it.
  const firedRef = useRef(false);
  useEffect(() => {
    if (!isComplete || firedRef.current) return;
    firedRef.current = true;
    // Two short bursts from each side of the topbar feel more "celebratory" than one big blast.
    const burst = (origin) => confetti({
      particleCount: 70,
      spread: 65,
      startVelocity: 45,
      origin,
      colors: ["#6EE7C5", "#7FB3FF", "#2D7BFF", "#ffffff", "#FFC97D"],
      ticks: 220,
      scalar: 0.85,
      disableForReducedMotion: true,
    });
    burst({ x: 0.15, y: 0.15 });
    setTimeout(() => burst({ x: 0.85, y: 0.15 }), 180);
  }, [isComplete]);

  return (
    <div
      className="sticky top-0 z-40 text-white border-b backdrop-blur"
      style={{ background: "#0F2B5B", borderColor: "rgba(127,179,255,0.18)" }}
      data-testid="studio-inline-topbar"
      data-completion={isComplete ? "complete" : "in-progress"}
    >
      <div className="h-12 px-3 lg:px-5 flex items-center gap-3 lg:gap-4 max-w-[1600px] mx-auto">
        <Link
          to="/studio"
          className="inline-flex items-center gap-1.5 text-[12.5px] hover:text-white transition"
          style={{ color: "#93B4D4" }}
          data-testid="topbar-back"
        >
          <ArrowLeft size={14} /> Studio
        </Link>

        <div className="hidden sm:flex items-center gap-2 flex-1 min-w-0">
          <span className="text-[12.5px] shrink-0" style={{ color: "#93B4D4" }}>Aperçu sandbox —</span>
          <span className="text-[13px] font-medium truncate text-white" data-testid="topbar-brand">
            {brandName || "Votre plateforme"}
          </span>
          {isSaving && (
            <span className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.18em]" style={{ color: "#4FC3F7" }} data-testid="topbar-saving">
              <Loader2 size={10} className="animate-spin" /> Enregistrement…
            </span>
          )}
        </div>

        {isComplete ? (
          <span
            className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold"
            style={{ background: "#4FC3F7", color: "#0F2B5B", boxShadow: "0 0 24px rgba(79,195,247,0.45)" }}
            data-testid="topbar-complete-badge"
          >
            <PartyPopper size={13} />
            Configuration complète
          </span>
        ) : (
          <div className="hidden md:flex items-center gap-2.5 px-3" data-testid="topbar-progress">
            <div className="h-1.5 w-28 lg:w-40 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.10)" }}>
              <div className="h-full transition-all" style={{ width: `${pct}%`, background: "#4FC3F7" }} />
            </div>
            <span className="text-[11.5px] tabular-nums shrink-0" style={{ color: "#B8D4E8" }}>
              <strong className="text-white">{answered}</strong> / {total} questions
            </span>
          </div>
        )}

        <button
          onClick={onOpenDrawer}
          data-testid="topbar-open-drawer"
          className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-full text-white hover:brightness-110 transition"
          style={{ background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.20)" }}
        >
          <ListChecks size={13} />
          <span className="hidden lg:inline">Voir toutes les questions</span>
          <span className="lg:hidden">Questions</span>
        </button>

        <button
          onClick={onPublish}
          disabled={!isDraft || isPublishing || answered === 0}
          data-testid="topbar-publish"
          title={
            !isDraft
              ? "Projet déjà finalisé"
              : answered === 0
              ? "Répondez à au moins une question pour finaliser"
              : isComplete
              ? "Toutes les questions sont remplies — finalisez votre cahier des charges"
              : "Finaliser et recevoir le PDF par email"
          }
          className={`inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-full font-semibold hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition ${
            isComplete && isDraft && !isPublishing ? "animate-pulse-mint" : ""
          }`}
          style={{
            background: "#4FC3F7",
            color: "#0F2B5B",
            boxShadow: isComplete && isDraft && !isPublishing ? "0 0 18px rgba(79,195,247,0.5)" : "none",
          }}
        >
          {isPublishing ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
          {isPublishing ? "Envoi…" : isComplete ? "Finaliser maintenant" : "Finaliser"}
        </button>
      </div>
    </div>
  );
}
